// src/utils/timeout.js — helpers para que ningún await de red cuelgue el boot.
//
// Auditoría "app tildada" (5-jun-2026): los awaits a Supabase del bootstrap
// (getSession + usuarios + cargarMisGrupos) no tenían timeout. Si la red colgaba,
// el primer render nunca corría y la card de carga quedaba para siempre.

// Tope estándar para llamadas de red de la app. Mobile-ready (6-jun): en
// mobile/cold-start una RPC legítima puede tardar 5-8s; 12s evita matar
// requests reales sin dejar que un hang dure indefinidamente.
export const NET_TIMEOUT_MS = 12000

// Corre `promise` contra un timeout. Si no resuelve en `ms`, rechaza con
// Error('timeout:<label>'). Limpia el timer pase lo que pase. Para promesas que
// NO se pueden abortar (ej. supabase.auth.getSession): la original sigue viva en
// background, pero el caller ya no la espera → el boot avanza igual.
export const withTimeout = (promise, ms, label = 'op') => {
  let t
  const timeout = new Promise((_, reject) => {
    t = setTimeout(() => reject(new Error(`timeout:${label}`)), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(t))
}

// fetch con timeout DURO vía AbortController + setTimeout. NO usa
// AbortSignal.timeout (no existe en WebView viejos — Redmi Note 8 Pro / Android
// 11 / MIUI 2022). Compone con el signal que el caller pase (ej. supabase con
// .abortSignal): aborta si vence el timeout O si el caller aborta. Pensado para
// el `global.fetch` del cliente Supabase → da timeout a TODA request
// (auth/rest/storage + el refresh silencioso de autoRefreshToken). Devuelve una
// función fetch lista para `createClient({ global: { fetch } })`.
export const fetchWithTimeout = (ms = NET_TIMEOUT_MS) => (input, init = {}) => {
  const controller = new AbortController()
  const upstream = init.signal
  if (upstream) {
    if (upstream.aborted) controller.abort()
    else upstream.addEventListener('abort', () => controller.abort(), { once: true })
  }
  return new Promise((resolve, reject) => {
    let settled = false
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      controller.abort() // corta el socket (libera recursos)
      // CRÍTICO (causa raíz freeze mobile, 6-jun): al vencer el timeout NO
      // rechazamos con AbortError. auth-js convierte cualquier throw del fetch en
      // AuthRetryableFetchError (lib/fetch.js) → el auto-refresh de GoTrue
      // (retryable, attempt<Infinity) entra en un retry-storm de ~60s sobre la
      // conexión muerta. Un Response 408 NO está en sus NETWORK_ERROR_CODES
      // [502,503,504,520-530] → GoTrue lo trata como error FINAL, no reintenta.
      // (PostgREST/storage también reciben un 408 limpio = error, sin retry.)
      resolve(new Response(JSON.stringify({ message: 'client timeout' }), {
        status: 408,
        headers: { 'Content-Type': 'application/json' },
      }))
    }, ms)
    fetch(input, { ...init, signal: controller.signal }).then(
      (r) => { if (settled) return; settled = true; clearTimeout(timer); resolve(r) },
      // Antes del timeout: abort upstream (el caller abortó vía .abortSignal, ej.
      // queryWithTimeout) o error real de red → propagamos el rechazo como hoy.
      (e) => { if (settled) return; settled = true; clearTimeout(timer); reject(e) },
    )
  })
}

// Corre una query de Supabase ABORTABLE de verdad: `buildQuery(signal)` debe
// devolver el thenable con `.abortSignal(signal)` aplicado. A los `ms` se aborta
// el fetch (AbortController), no se deja la promesa colgada. supabase resuelve
// con `{ data:null, error }` al abortar; si alguna versión tirara, el caller lo
// atrapa. Limpia el timer siempre.
export const queryWithTimeout = async (buildQuery, ms, label = 'query') => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    return await buildQuery(controller.signal)
  } finally {
    clearTimeout(timer)
  }
}
